import { gql } from "@apollo/client";
import type { SyncableVtm } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Vtm as RxVtm } from "../collections/vtm";
import type { RxDBCollections } from "../provider";

import {
  SyncVtmPullDocument,
  type WholeSyncableVtmFragment,
} from "./__generated__/vtm.gql";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateVtm = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxVtm>;
}) => {
  const pull = useMemo(
    () => ({
      query: SyncVtmPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    }),
    [],
  );

  return useGqlReplication<RxVtm, SyncableVtm, null>({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlVtmToRxVtm,
    batchSize: 1000,
    metadataLocalStorageKeySuffix: "vtm",
  });
};

const mapGqlVtmToRxVtm = (
  vtm: WholeSyncableVtmFragment,
): WithDeleted<RxVtm> => ({
  id: vtm.id,
  title: vtm.title,
  keywords: [...vtm.keywords],
  snomedId: vtm.snomedId ?? null,
  productType: vtm.productType,
  meta: {
    createdAt: vtm.meta.createdAt,
    createdByUserId: vtm.meta.createdByUserId,
    updatedAt: vtm.meta.updatedAt,
    updatedByUserId: vtm.meta.updatedByUserId,
  },
  _deleted: vtm.meta.deleted,
});

gql`
  fragment WholeSyncableVtm on SyncableVtm {
    id
    title
    keywords
    productType
    snomedId
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncVtmPull($input: PullVtmInput!) {
    pullVtm(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableVtm
      }
    }
  }
`;
